import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs/internal/Observable';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ActivatedRoute } from '@angular/router';

export interface TinyUrlResponse {
    data: {
        tiny_url: string;
    };
}

interface UpdateTemplateResponse {
    data: {
        results: {
            app_id: string;
            device_id: string;
            result: string;
            status_code: number;
        }[];
    };
    success: boolean;
}

@Component({
    selector: 'update-device-screen',
    templateUrl: './update-device-screen.component.html',
})
export class UpdateDeviceScreenComponent implements OnInit {
    templateMessage = 'Collez ici votre nouveau message';
    templateUrl = 'Collez ici votre url';
    deviceId = '';
    urlBase = environment.apiUrl; // Stocke l'url.
    tinyurlApiKey = environment.tinyUrl.tinyurlApiKey;
    tinyurlApiUrl = environment.tinyUrl.tinyurlUrl;
    imageSrc: string | null = null;
    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    originalTemplateUrl = '';
    isImageRefreshing = false;
    shortenedUrlAlreadyGenerated = '';
    id_user = 87141; //TODO le tranformer en valeur qui vient d'ailleurs
    errorMessage = '';
    warnMessage = '';

    constructor(
        private http: HttpClient,
        private route: ActivatedRoute,
    ) { }

    ngOnInit(): void {
        this.route.queryParams.subscribe(params => {
            this.deviceId = params['deviceId'] || '';
        });
        this.fetchTemplates()
    }

    onError(place: string, error: unknown): void {
        if (error instanceof Error) {
            this.errorMessage = `${place} : ${error.message}`;
            console.error(this.errorMessage);
        } else if (typeof error === 'string') {
            this.warnMessage = `${place} : ${error}`;
            console.warn(this.warnMessage);
        } else {
            this.warnMessage = `${place} : Erreur inconnue`;
            console.warn(this.warnMessage, error);
        }
    }


    async getBaseApi<T>(endpoint: string, params: Record<string, unknown> = {}): Promise<T> {
        const queryParams = new URLSearchParams();

        for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== null) {
                queryParams.set(key, String(value));
            }
        }

        const url = `${this.urlBase}${endpoint}?${queryParams.toString()}`;

        // ✅ Ici on force la promesse à toujours retourner quelque chose
        try {
            const res = await this.http.get<T>(url).toPromise();
            return res as T;
        } catch (err) {
            this.onError('API error:', err);
            throw err; // ou return {} as T si tu veux forcer vide
        }
    }

    async fetchTemplates() {
        if (!this.deviceId) {
            console.warn('⚠️ Aucun deviceId fourni à fetchTemplates');
            return;
        }
        try {
            const response = await this.getBaseApi<{ data: { job_id: string, content: string } }>('get-template', {
                device_id: this.deviceId,
                id_user: this.id_user,
            });
            if (response?.data?.content) {
                this.imageSrc = response.data.content;
            } else {
                console.warn("⚠️ Aucune image dans la réponse, contenu manquant ou vide.");
            }
        } catch (error: any) {
            if (error?.status === 401) {
                this.onError("fetchTemplates 401", "Session expirée ou identifiants invalides. Merci de vous reconnecter.");
            }  else if (error?.status === 400 &&  error.statusText === "OK") {
                this.onError("fetchTemplates 400", "Impossible de contacter le device. Vérifie qu'il est allumé et connecté.");
            } else if (error?.status === 500) {
                this.onError("fetchTemplates 500", "Erreur interne du serveur. Réessaie plus tard.");
            } else {
                this.onError("fetchTemplates other", "Erreur inattendue. Détails : " + ( error.statusText || "aucun"));
            }
        }
    }

    async postBaseApi<T>(endpoint: string, param: Record<string, unknown> = {}): Promise<T> {
        try {
            const result = await this.http.post<T>(this.urlBase + endpoint, param, {
                headers: { "Content-Type": "application/json" }
            }).toPromise();
            return result as T;
        } catch (error) {
            this.onError(`POST ${endpoint}`, error);
            throw error;
        }
    }


    async updateDeviceWithTemplate() {
        let finalUrl = this.templateUrl;
        try {
            const urlIsPlaceholder = this.templateUrl.trim().toLowerCase() === 'collez ici votre url';
            const urlHasChanged = this.templateUrl !== this.originalTemplateUrl;
            // Cas 1 : L'utilisateur a changé l'URL depuis la valeur initiale
            if (!urlIsPlaceholder && urlHasChanged) {
                const shortenedUrl = await this.shortenUrl(this.templateUrl).toPromise();
                if (shortenedUrl) {
                    finalUrl = shortenedUrl;
                    this.shortenedUrlAlreadyGenerated = shortenedUrl;
                    this.originalTemplateUrl = this.templateUrl; // remet à jour l'original
                }
            }
            // Cas 2 : L'utilisateur n’a PAS changé l’URL, mais l’a déjà raccourcie une fois
            else if (this.shortenedUrlAlreadyGenerated) {
                finalUrl = this.shortenedUrlAlreadyGenerated;
            }

            const body = {
                device_id: this.deviceId,
                message: this.templateMessage,
                url: finalUrl,
                id_user: this.id_user
            };
            const res = await this.postBaseApi<UpdateTemplateResponse>('update-template', body);
            this.isImageRefreshing = true;
            await this.delay(68000); // attendre que l’image soit à jour côté API
            await this.fetchTemplates(); // Refresh du template
        } catch (err) {
            console.error('❌ Erreur lors de l’envoi au backend :', err)
            alert('❌ Une erreur est survenue pendant la mise à jour.');
        } finally {
            this.isImageRefreshing = false;
        }
    }

    shortenUrl(longUrl: string): Observable<string> {
        const headers = new HttpHeaders({
            Authorization: `Bearer ${this.tinyurlApiKey}`,
            'Content-Type': 'application/json',
        });
        const body = {
            url: longUrl,
            domain: 'tiny.one', // ou tinyurl.com
        };
        const result = this.http.post<TinyUrlResponse>(this.tinyurlApiUrl, body, { headers }).pipe(
            map(res => res.data.tiny_url)
        );
        return result
    }

    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;
        if (file.size > 23000) {
            alert('❌ Image trop lourde : elle doit faire moins de 23 KB (PNG brut).');
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result as string;
            const img = new Image();
            img.onload = () => {
                this.sendImageUpdate(this.deviceId, base64);
            };
            img.src = base64;
        };
        reader.readAsDataURL(file);
    }

    async sendImageUpdate(deviceId: string, base64Image: string) {
        try {
            const body = {
                device_id: this.deviceId,
                base_64_image: base64Image,
                id_user: this.id_user
            };
            const res = await this.postBaseApi<UpdateTemplateResponse>('send-png', body);
            // 🔍 Vérifie si la mise à jour a vraiment réussi
            const result = res?.data?.results?.[0];
            if (result.status_code === 404 && result.result === 'Offline') {
                alert(`❌ Le device n'est pas connecté.`);
                return;
            }
            this.isImageRefreshing = true;
            await this.delay(8000); // attendre que l’image soit à jour côté API
            await this.fetchTemplates(); // Refresh du template
        } catch (err) {
            console.error('❌ Erreur lors de l’envoi au backend :', err)
            alert('❌ Une erreur est survenue pendant la mise à jour.');
        } finally {
            this.isImageRefreshing = false;
        }
    }
}
